import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, vShow as _vShow, withDirectives as _withDirectives, createVNode as _createVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createTextVNode as _createTextVNode, withCtx as _withCtx, createBlock as _createBlock, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "relative-position full-height video-container" }
const _hoisted_2 = /*#__PURE__*/_createElementVNode("div", { class: "scanner-laser" }, null, -1)
const _hoisted_3 = { class: "column fit-column vertical-middle justify-center" }
const _hoisted_4 = {
  key: 0,
  class: "column items-center justify-evenly"
}
const _hoisted_5 = { class: "q-mt-md" }

import { Camera } from "@src-capacitor/@capacitor/camera"
import { BrowserMultiFormatReader, IScannerControls } from "@zxing/browser"
import { ChecksumException, FormatException, NotFoundException } from "@zxing/library"
import { Platform } from "quasar"
import SfxBtn from "src/components/button/SfxBtn.vue"
import { Log } from "src/util/classes/log"
import { Icons } from "src/util/enums/fontAwesomeIcons"
import { onMounted, onUnmounted, ref } from "vue"
import { useI18n } from "vue-i18n"

// NOTE: This might be useful to look at in the future https://github.com/capacitor-community/barcode-scanner

/**
 * Component used for scanning QR and DataMatrix codes.
 *
 * This component will not work when debugging on Android with the code being served from a PC.
 */

//region Props and Emits

/**
  - "decode" - sends the message we want to parse
  - "camera-stop" - sends whenever the camera has stopped working
 */

export default /*#__PURE__*/_defineComponent({
  __name: 'SfxScannerBarcode',
  emits: ["decode", "camera-stop"],
  setup(__props, { emit: __emit }) {

const emit = __emit

//endregion

//region Variables

const { t } = useI18n()
let showScanner = ref(false)
let showRetryButton = ref(false)
let warningMessage = ref("")
const codeReader = new BrowserMultiFormatReader()
const scanner = ref<HTMLVideoElement | undefined>(undefined)
let readerHandler: IScannerControls
const componentIsActive = ref(false)

//endregion

//region Lifecycle

onMounted(async () => {
  componentIsActive.value = true
  await initScan()
})

onUnmounted(() => {
  componentIsActive.value = false
  if (readerHandler) {
    readerHandler.stop()
    emit("camera-stop")
  }
})

//endregion

//region Functions

async function initScan() {
  const isMediaStreamAPISupported =
    navigator && navigator.mediaDevices && "enumerateDevices" in navigator.mediaDevices
  if (!isMediaStreamAPISupported) {
    warningMessage.value = t("sfxScanners.missing_camera_warning")
    return
  }
  try {
    const hasPermission = await getCameraPermission()

    if (!hasPermission) {
      showRetryButton.value = true
      warningMessage.value = t("sfxScanners.missing_camera_permission_warning")

      if (!Platform.is.capacitor) {
        warningMessage.value += " " + t("sfxScanners.missing_browser_camera_permission_warning")
      }
      return
    }
    await startDecoder()
  } catch (error) {
    Log.error({ message: "Failed to initiate scanning.", error })
  }
}

async function getCameraPermission() {
  if (Platform.is.capacitor) {
    let permissions = await Camera.checkPermissions()
    if (permissions.camera !== "granted") {
      permissions = await Camera.requestPermissions()
    }
    return permissions.camera === "granted"
  } else {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: false, video: true })
      const videoTracks = stream.getVideoTracks()
      return videoTracks.length > 0
    } catch (error) {
      if (error instanceof DOMException && error.name === "NotAllowedError") {
        return false
      } else {
        throw error
      }
    }
  }
}

async function reloadPage() {
  await initScan()
}

/**
 * Continuously tries to decode a barcode and when successful, it emits the event.
 */
async function startDecoder() {
  warningMessage.value = ""
  showScanner.value = true

  // NOTE: In case we want to restrict to specific formats
  // codeReader.possibleFormats = [BarcodeFormat.QR_CODE]

  readerHandler = await codeReader.decodeFromVideoDevice(
    undefined,
    scanner.value,
    (value, err, controls) => {
      if (value) {
        emit("decode", value)
      }

      if (err) {
        handleScannerErrors(err)
      }

      if (!componentIsActive.value) {
        // Component has been destroyed. Make sure to remove this stream.
        controls.stop()
        emit("camera-stop")
      }
    }
  )
}

function handleScannerErrors(err: Error) {
  if (err instanceof FormatException) {
    warningMessage.value = t("sfxScanners.invalid_code_format_warning")
  } else if (err instanceof ChecksumException) {
    warningMessage.value = t("sfxScanners.invalid_code_value_warning")
  } else if (!(err instanceof NotFoundException)) {
    Log.error({ message: "Unexpected error has occurred.", error: err })
  }
}

//endregion

return (_ctx: any,_cache: any) => {
  const _component_q_icon = _resolveComponent("q-icon")!
  const _component_q_banner = _resolveComponent("q-banner")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _withDirectives(_createElementVNode("div", _hoisted_1, [
      _createElementVNode("video", {
        class: "full-height full-width video-element",
        ref_key: "scanner",
        ref: scanner
      }, null, 512),
      _hoisted_2
    ], 512), [
      [_vShow, _unref(showScanner)]
    ]),
    _createElementVNode("div", _hoisted_3, [
      (!_unref(showScanner) && _unref(showRetryButton) && _ctx.$q.platform.is.capacitor)
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
            _createVNode(SfxBtn, {
              icon: _unref(Icons).ARROW_ROTATE_RIGHT,
              class: "text-overline text-uppercase",
              color: "white",
              "text-color": "navy-base",
              onClick: reloadPage
            }, null, 8, ["icon"]),
            _createElementVNode("p", _hoisted_5, _toDisplayString(_ctx.$t("sfxButtons.btn_retry")), 1)
          ]))
        : _createCommentVNode("", true),
      (_unref(warningMessage))
        ? (_openBlock(), _createBlock(_component_q_banner, {
            key: 1,
            class: "bg-warning text-white"
          }, {
            avatar: _withCtx(() => [
              _createVNode(_component_q_icon, {
                name: _unref(Icons).WARNING,
                color: "white"
              }, null, 8, ["name"])
            ]),
            default: _withCtx(() => [
              _createTextVNode(" " + _toDisplayString(_unref(warningMessage)), 1)
            ]),
            _: 1
          }))
        : _createCommentVNode("", true)
    ])
  ], 64))
}
}

})